import { logger } from '@axon/ui/utils';

import type {
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  RefreshTokenResponse,
  VerifyResponse,
  User,
} from '@/types/auth';

class AuthService {
  private readonly basePath = '/api/auth';

  private parseExpiresIn(expiresIn: string | number): number {
    if (typeof expiresIn === 'number') {
      return expiresIn;
    }

    // Parse string formats like "2h", "24h", "1h"
    const match = expiresIn.match(/^(\d+)([hms])$/);
    if (!match) {
      logger.warn('Invalid expiresIn format, defaulting to 2 hours', {
        expiresIn,
        defaultValue: 7200,
      });
      return 7200; // 2 hours default
    }

    const [, value, unit] = match;
    const num = parseInt(value, 10);

    switch (unit) {
      case 'h':
        return num * 3600; // hours to seconds
      case 'm':
        return num * 60; // minutes to seconds
      case 's':
        return num; // already seconds
      default:
        return 7200; // 2 hours default
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(this.basePath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
      credentials: 'include',
    });

    if (!response.ok) {
      const defaultMessage = 'Login failed';
      const error = await response.json().catch(() => ({ message: defaultMessage }));
      const errorMessage = error.message || defaultMessage;
      throw new Error(errorMessage);
    }

    const data = await response.json();

    // Backend returns: { accessToken, refreshToken, tokenType, expiresIn, user }
    // Parse expiresIn to ensure it's always a number in seconds
    const expiresInSeconds = this.parseExpiresIn(data.expiresIn || 7200);

    return {
      user: data.user,
      accessToken: data.accessToken,
      expiresIn: expiresInSeconds,
    };
  }

  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    // For registration, we need to call the backend directly
    const response = await fetch(`/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    await response.json();

    // After successful registration, login automatically
    return this.login({
      email: credentials.email,
      password: credentials.password,
    });
  }

  async logout(): Promise<void> {
    try {
      await fetch(this.basePath, {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch {
      // Silently handle logout failures - user experience is more important
      // than perfect logout reliability
    }
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const response = await fetch(`${this.basePath}/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      // Enhanced error reporting for debugging
      const errorDetails = {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      };

      // Correct logger.error signature: error(message, error?, context?)
      logger.error('Token refresh failed', undefined, errorDetails);

      throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Backend now returns: { accessToken, refreshToken, tokenType, expiresIn, user }
    const expiresInSeconds = this.parseExpiresIn(data.expiresIn || 7200);

    return {
      user: data.user,
      accessToken: data.accessToken,
      expiresIn: expiresInSeconds,
    };
  }

  async verify(): Promise<VerifyResponse> {
    try {
      const response = await fetch(this.basePath, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${this.getStoredToken()}`,
        },
      });

      if (!response.ok) {
        // Don't log 401 errors - they're expected when not authenticated
        if (response.status !== 401) {
          logger.error('Auth verification failed', {
            status: response.status,
            statusText: response.statusText,
          });
        }
        throw new Error('Not authenticated');
      }

      // Next.js /api/auth returns { user: User }, not User directly.
      // Unwrap so callers get the actual user object.
      const data = await response.json();
      const user = data.user ?? data;

      return {
        user,
      };
    } catch (error) {
      // Silently fail for network errors during auth check
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Network error, silently fail
        throw new Error('Network error');
      }
      throw error;
    }
  }

  private getStoredToken(): string {
    // Get token from auth store
    if (typeof window !== 'undefined') {
      try {
        const authState = JSON.parse(localStorage.getItem('tms-auth-storage') || '{}');
        return authState.state?.token || '';
      } catch {
        return '';
      }
    }
    return '';
  }

  async forgotPassword(email: string): Promise<void> {
    const response = await fetch(`/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send reset email');
    }
  }

  async resetPassword(token: string, password: string): Promise<void> {
    const response = await fetch(`/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset password');
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const response = await fetch(`/api/v1/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ oldPassword, newPassword }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to change password');
    }
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await fetch(`/api/v1/auth/profile`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update profile');
    }

    return response.json();
  }
}

export const authService = new AuthService();
